import React, { useState, useEffect } from "react";
import {
  Card,
  Descriptions,
  Tag,
  Button,
  message,
  Statistic,
  Row,
  Col,
  Divider,
  Alert,
  Space,
} from "antd";
import {
  ReloadOutlined,
  DatabaseOutlined,
  CloudServerOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import apiService from "../../services/api";
import ErrorBoundary from "../common/ErrorBoundary";
import "./Settings.css";

const Settings = () => {
  const [systemInfo, setSystemInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dbStats, setDbStats] = useState(null);

  useEffect(() => {
    fetchSystemInfo();
    fetchDbStats();
  }, []);

  const fetchSystemInfo = async () => {
    try {
      setLoading(true);
      const response = await apiService.admin.getSystemInfo();
      setSystemInfo(response.data?.data || null);
    } catch (err) {
      message.error("获取系统信息失败");
    } finally {
      setLoading(false);
    }
  };

  const fetchDbStats = async () => {
    try {
      const response = await apiService.getDbStats();
      // extracting the nested database info correctly from health check
      setDbStats(response.data?.data || null);
    } catch (err) {
      // 静默失败
    }
  };

  const handleRefresh = () => {
    fetchSystemInfo();
    fetchDbStats();
    message.success("已刷新系统信息");
  };

  return (
    <ErrorBoundary>
      <div className="settings-container">
        <div className="settings-header">
          <h1 className="settings-title">系统设置</h1>
          <Button
            className="refresh-btn"
            icon={<ReloadOutlined />}
            onClick={handleRefresh}
          >
            刷新
          </Button>
        </div>

        <Alert
          message="系统设置页面仅供管理员查看系统运行状态，配置修改请直接编辑 config.yaml 文件。"
          type="info"
          showIcon
          className="info-alert"
        />

        {/* 系统运行信息 */}
        <Card
          title={
            <>
              <CloudServerOutlined /> 系统运行信息
            </>
          }
          loading={loading}
          className="system-info-card"
        >
          {systemInfo ? (
            <>
              <Row gutter={16} className="stats-row">
                <Col xs={12} sm={6}>
                  <Statistic title="CPU 核心数" value={systemInfo.num_cpu} />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="Goroutine 数"
                    value={systemInfo.num_goroutine}
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="内存使用"
                    value={systemInfo.memory?.alloc_mb || 0}
                    suffix="MB"
                  />
                </Col>
                <Col xs={12} sm={6}>
                  <Statistic
                    title="GC 次数"
                    value={systemInfo.memory?.gc_count || 0}
                  />
                </Col>
              </Row>

              <Divider />

              <Descriptions
                column={2}
                bordered
                size="small"
                className="info-descriptions"
              >
                <Descriptions.Item label="Go 版本">
                  <Tag color="blue">{systemInfo.go_version}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="操作系统">
                  {systemInfo.os}/{systemInfo.arch}
                </Descriptions.Item>
                <Descriptions.Item label="系统时间">
                  {systemInfo.server_time
                    ? new Date(systemInfo.server_time).toLocaleString("zh-CN")
                    : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="系统内存">
                  {systemInfo.memory?.sys_mb || 0} MB
                </Descriptions.Item>
              </Descriptions>
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">ℹ️</div>
              <div>暂无系统信息</div>
            </div>
          )}
        </Card>

        {/* 数据库信息 */}
        <Card
          title={
            <>
              <DatabaseOutlined /> 数据库状态
            </>
          }
          className="database-info-card"
        >
          {dbStats ? (
            <Descriptions
              column={2}
              bordered
              size="small"
              className="info-descriptions"
            >
              <Descriptions.Item label="状态">
                <span
                  className={`status-tag ${dbStats?.database === "healthy" ? "healthy" : "unhealthy"}`}
                >
                  {dbStats?.database === "healthy" ? "正常" : "异常"}
                </span>
              </Descriptions.Item>
              <Descriptions.Item label="数据库类型">SQLite3</Descriptions.Item>
              <Descriptions.Item label="最大连接数">
                <code className="code-block">
                  {dbStats?.database_stats?.max_open_connections || "-"}
                </code>
              </Descriptions.Item>
              <Descriptions.Item label="当前连接数">
                <code className="code-block">
                  {dbStats?.database_stats?.open_connections || "-"}
                </code>
              </Descriptions.Item>
              <Descriptions.Item label="使用中连接">
                <code className="code-block">
                  {dbStats?.database_stats?.in_use || "0"}
                </code>
              </Descriptions.Item>
              <Descriptions.Item label="空闲连接">
                <code className="code-block">{dbStats?.database_stats?.idle || "0"}</code>
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <div className="empty-state">
              <div className="empty-icon">🗄️</div>
              <div className="empty-text">暂无数据库信息</div>
            </div>
          )}
        </Card>

        {/* 系统配置说明 */}
        <Card
          title={
            <>
              <SettingOutlined /> 配置说明
            </>
          }
          className="config-info-card"
        >
          <Descriptions
            column={1}
            bordered
            size="small"
            className="info-descriptions"
          >
            <Descriptions.Item label="配置文件路径">
              <code className="code-block">backend/config/config.yaml</code>
            </Descriptions.Item>
            <Descriptions.Item label="数据库文件路径">
              <code className="code-block">backend/data/trade_volume.db</code>
            </Descriptions.Item>
            <Descriptions.Item label="JWT 过期时间">
              修改 <code className="code-block">jwt.expires_in</code>
              （单位：小时）
            </Descriptions.Item>
            <Descriptions.Item label="服务端口">
              修改 <code className="code-block">server.port</code>（默认：9080）
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </div>
    </ErrorBoundary>
  );
};

export default Settings;
